/** Unicode-capable font for jsPDF (supports ₪, €, £, etc.). */
export const PDF_FONT_FAMILY = 'PdfSans';
const PDF_FONT_FILE = 'PdfSans.ttf';
const PDF_FONT_URL = `${process.env.PUBLIC_URL || ''}/fonts/PdfSans.ttf`;

let fontBinaryCache = null;
let fontLoadPromise = null;

function arrayBufferToBinaryString(buffer) {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 8192;
  let result = '';
  for (let i = 0; i < bytes.length; i += chunkSize) {
    result += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
  }
  return result;
}

async function loadPdfFontBinary() {
  if (fontBinaryCache) return fontBinaryCache;
  if (!fontLoadPromise) {
    fontLoadPromise = fetch(PDF_FONT_URL)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load PDF font (${response.status})`);
        }
        return response.arrayBuffer();
      })
      .then((buffer) => {
        fontBinaryCache = arrayBufferToBinaryString(buffer);
        return fontBinaryCache;
      });
  }
  return fontLoadPromise;
}

export function registerPdfUnicodeFontStyles(doc) {
  const families = doc.getFontList?.() || {};
  const family = families[PDF_FONT_FAMILY];
  if (!family) {
    doc.addFont(PDF_FONT_FILE, PDF_FONT_FAMILY, 'normal');
    doc.addFont(PDF_FONT_FILE, PDF_FONT_FAMILY, 'bold');
    return;
  }
  if (!family.bold) {
    doc.addFont(PDF_FONT_FILE, PDF_FONT_FAMILY, 'bold');
  }
}

/** Register and select the PDF Unicode font on a jsPDF document. */
export async function applyPdfUnicodeFont(doc) {
  const binary = await loadPdfFontBinary();
  if (!doc.getFileFromVFS(PDF_FONT_FILE)) {
    doc.addFileToVFS(PDF_FONT_FILE, binary);
  }
  registerPdfUnicodeFontStyles(doc);
  doc.setFont(PDF_FONT_FAMILY, 'normal');
}

export function pdfTableFontStyles(overrides = {}) {
  return { font: PDF_FONT_FAMILY, ...overrides };
}

/** Hebrew, Arabic, and their presentation-form Unicode ranges. */
const RTL_CHAR_REGEX = /[\u0591-\u07FF\uFB1D-\uFDFD\uFE70-\uFEFC]/;

/** True when the string contains at least one Hebrew/Arabic character. */
export function containsRtlText(text) {
  return RTL_CHAR_REGEX.test(String(text ?? ''));
}

/**
 * jsPDF/jspdf-autotable draw text glyph-by-glyph in string order with no bidi
 * support, so raw Hebrew/Arabic substrings render with mirrored (backwards) letter
 * order instead of proper right-to-left. This rewrites each contiguous RTL "run"
 * (one or more RTL words, e.g. within an otherwise-English label) into the visual
 * order jsPDF needs to draw correctly left-to-right: letters reversed within each
 * RTL word, and word order reversed within the run — while any surrounding LTR
 * text, numbers, and punctuation stay untouched and in their original position.
 */
export function toPdfVisualText(text) {
  const str = String(text ?? '');
  if (!containsRtlText(str)) return str;

  const tokens = str.split(/(\s+)/);
  const result = [...tokens];
  let runStart = -1;

  const flushRun = (endExclusive) => {
    if (runStart === -1) return;
    // Trailing whitespace tokens are the separator *after* the run (before the next
    // LTR token); leave them in place rather than pulling them into the reversal.
    let end = endExclusive;
    while (end > runStart && /^\s+$/.test(result[end - 1])) end -= 1;
    const run = result.slice(runStart, end);
    const reversedRun = run
      .map((tok) => (containsRtlText(tok) ? tok.split('').reverse().join('') : tok))
      .reverse();
    for (let i = 0; i < reversedRun.length; i += 1) {
      result[runStart + i] = reversedRun[i];
    }
    runStart = -1;
  };

  tokens.forEach((tok, i) => {
    if (containsRtlText(tok)) {
      if (runStart === -1) runStart = i;
    } else if (!/^\s+$/.test(tok)) {
      flushRun(i);
    }
  });
  flushRun(tokens.length);

  return result.join('');
}

/** Apply {@link toPdfVisualText} to every string cell in a table row; other cell types pass through. */
export function toPdfVisualRow(row) {
  if (!Array.isArray(row)) return row;
  return row.map((cell) => (typeof cell === 'string' ? toPdfVisualText(cell) : cell));
}
